import { useCallback, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useTestFunc } from '@carvana/test-attributes';

import Button from '@carvana/ds-button';
import {
  TileCard,
  ImageWrapper,
  ContentWrapper,
  PriceExpirationWrapper,
  VehicleInfoWrapper,
  VehicleValue,
  YearMakeModel
} from './SummaryTiles.styles';
import { useVehicleUrls, useBadge, useVehicleDisplayData, useButtonText } from './hooks';
import { LifecycleState } from 'api/GraphQL/queries/getVehicleSummaries';
import type { VehicleSummary } from 'api/GraphQL/queries/getVehicleSummaries';
import useAnalyticEvent from 'shared/hooks/useAnalyticEvent';
import useImageQuery from 'shared/hooks/useImageQuery';

type SummaryTileProps = {
  summary: VehicleSummary;
  index?: number;
  isMobile?: boolean;
  tileCount?: number;
};

const SummaryTile = ({ summary, index, isMobile, tileCount }: SummaryTileProps) => {
  const router = useRouter();
  const { recordActionClicked } = useAnalyticEvent();
  const getTestAttributes = useTestFunc();

  const {
    acquisitionName,
    appraisalId,
    color,
    daysToExpiration,
    isOfferAccepted,
    lifecycleState,
    make,
    model,
    trim,
    vehicleValue,
    year
  } = summary;

  // Fetch vehicle image
  const { data: imageUrl } = useImageQuery({
    year: year || 0,
    make: make || '',
    model: model || '',
    trim: trim || '',
    color: color || '',
    isMobile: true // Always fetch mobile size for tiles
  });

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const showImage = !!imageUrl && !imageError;

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [imageUrl]);

  // Fallback to no-image variant if image doesn't load within 5 seconds
  useEffect(() => {
    if (!imageUrl || imageLoaded || imageError) return undefined;
    const timeout = setTimeout(() => setImageError(true), 5000);
    return () => clearTimeout(timeout);
  }, [imageUrl, imageLoaded, imageError]);

  // Extract URL construction logic
  const { url } = useVehicleUrls({
    acquisitionName,
    appraisalId,
    isOfferAccepted,
    lifecycleState
  });

  // Extract badge logic
  const badge = useBadge({ lifecycleState, daysToExpiration });

  // Extract display data calculations
  const { yearMakeModel, formattedValue } = useVehicleDisplayData({
    year,
    make,
    model,
    vehicleValue
  });

  // Extract button text logic
  const buttonText = useButtonText({ lifecycleState, isOfferAccepted });

  const handleClickTile = useCallback(async () => {
    await recordActionClicked(
      'Landing Page - Tile click',
      'AppraisalCard',
      { status: lifecycleState, summaryCount: tileCount },
      true
    );
    router.push(url);
  }, [recordActionClicked, lifecycleState, router, url, tileCount]);
  const showValue =
    lifecycleState === LifecycleState.Active ||
    lifecycleState === LifecycleState.Scheduled ||
    lifecycleState === LifecycleState.AppliedToPurchase;

  const largeTile = index === 0 && !isMobile;
  const firstTile = index === 0;

  return (
    <TileCard
      {...getTestAttributes('SummaryTile', appraisalId.toString())}
      onClick={handleClickTile}
      $size={isMobile ? 'md' : 'lg'}
    >
      <ContentWrapper $size={isMobile ? 'md' : 'lg'}>
        <ImageWrapper $hasImage={showImage} $size={largeTile ? 'lg' : 'md'}>
          {showImage && (
            <Image
              loader={({ src, width }) =>
                src.includes('?') ? `${src}&w=${width}` : `${src}?w=${width}`
              }
              src={imageUrl}
              alt={yearMakeModel}
              width={largeTile ? 131 : 90}
              height={largeTile ? 105 : 72}
              style={{ objectFit: 'cover' }}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}
        </ImageWrapper>
        <VehicleInfoWrapper>
          {badge}
          <YearMakeModel>{yearMakeModel}</YearMakeModel>
          {showValue && (
            <PriceExpirationWrapper>
              <VehicleValue $mobileSize={isMobile}>{formattedValue}</VehicleValue>
            </PriceExpirationWrapper>
          )}
        </VehicleInfoWrapper>
      </ContentWrapper>
      <Button
        size="md"
        width={isMobile ? '100%' : '165px'}
        onClick={handleClickTile}
        variant={firstTile ? 'primary' : 'tertiary'}
      >
        {buttonText}
      </Button>
    </TileCard>
  );
};

export default SummaryTile;
